import { useQueryClient } from '@tanstack/react-query';
import { noop } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Virtuoso } from 'react-virtuoso';
import {
  EventSearch,
  OnEventsSelectCallback,
} from 'src/components/Events/EventSearch';
import { EventSearchItem } from 'src/components/Events/EventSearch/EventSearchItem';
import { Content } from 'src/contexts/ContentContext';
import { Checkbox } from 'src/core/interim/Checkbox';
import { PosSelect } from 'src/core/POS/PosSelect';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { Button, Stack } from 'src/core/ui';
import { useEventGroupTicketGroups } from 'src/hooks/api/useEventGroupTicketGroups';
import {
  GET_EVENT_GROUPS_QUERY_KEY,
  useGetEventGroups,
} from 'src/hooks/api/useGetEventGroups';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { FieldError } from 'src/modals/common/Purchase';
import { ModalBodyDataContainer } from 'src/modals/Modal/Modal.styled';
import { CreateSeasonModal } from 'src/modals/Season/CreateSeasonModal';
import { ContentId } from 'src/utils/constants/contentId';
import { newBigIntId } from 'src/utils/idUtils';
import { posChangedField } from 'src/utils/posFieldUtils';
import { PurchaseTicketsInput } from 'src/utils/purchaseUtils';
import { DeliveryType, Feature, TicketGroupInput } from 'src/WebApiController';

import { useIsFromEventGroup } from '../PurchaseWizard.atom';
import {
  buildTicketGroupInput,
  copyTicketGroupToEvent,
} from './buildTicketGroupInput';
import * as styles from './SearchPurchasedEvents.css';

export function SearchPurchasedEvents({
  currencyCode,
  sourceTicketGroup,
  sourceTicketGroups,
}: {
  currencyCode: string;
  /** When set ("Copy tickets"), events picked here are added as copies of this group. */
  sourceTicketGroup?: TicketGroupInput | null;
  /** When set (event-level "Copy tickets"), each picked event gets a copy of each of these groups. */
  sourceTicketGroups?: TicketGroupInput[] | null;
}) {
  const { setValue, watch, formState, clearErrors } =
    useFormContext<PurchaseTicketsInput>();
  const ticketGroups = watch('ticketGroups');

  const ticketGroupFromPartialPurchase = watch(
    'ticketGroupFromPartialPurchaseOrder'
  );
  const searchTextPrefillForPartialPurchase = watch(
    'eventSearchTextFromPartialPurchaseOrder'
  );
  const selectedEventIds = useMemo(
    () =>
      ticketGroups
        ?.filter((tg) => tg.event?.virtId)
        .map((tg) => tg.event!.virtId),
    [ticketGroups]
  );

  // ── Create PO from a season (event) group ──────────────────────────────────
  // Event groups are standalone from POs — the same group can seed many POs, so
  // there is no restriction here. Checking the box swaps EventSearch for a group
  // dropdown; picking a group replaces ticketGroups with all of its members.
  const hasEventGroupFeature = useUserHasFeature(Feature.PlaceholderEventGroup);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [showCreateSeasonModal, setShowCreateSeasonModal] = useState(false);

  const queryClient = useQueryClient();
  const { data: eventGroups = [] } = useGetEventGroups();
  const eventGroupOptions = useMemo(
    () => Object.fromEntries(eventGroups.map((g) => [g.eventGroupId, g.name])),
    [eventGroups]
  );

  const [fromEventGroup, setFromEventGroup] = useIsFromEventGroup();
  useEffect(() => {
    return () => {
      setSelectedGroupId(null);
    };
  }, [setFromEventGroup]);

  // Hydrate the selected group's member slots into ticket groups (all members,
  // placeholders included). Disabled unless in season-group mode with a pick.
  const { ticketGroups: groupTicketGroups, isLoading: isGroupLoading } =
    useEventGroupTicketGroups(
      fromEventGroup ? selectedGroupId : null,
      currencyCode
    );

  // Replace the PO's ticket groups with the selected group's events once resolved.
  useEffect(() => {
    if (!fromEventGroup || !selectedGroupId) {
      return;
    }
    clearErrors('ticketGroups');
    setValue('ticketGroups', groupTicketGroups);
  }, [
    fromEventGroup,
    selectedGroupId,
    groupTicketGroups,
    setValue,
    clearErrors,
  ]);

  // Switching modes starts from a clean slate either way (unchecking clears the
  // group-derived ticket groups and returns to EventSearch).
  const onToggleFromEventGroup = useCallback(() => {
    setFromEventGroup((prev) => !prev);
    setSelectedGroupId(null);
    setValue('ticketGroups', []);
  }, [setFromEventGroup, setValue]);

  // After creating a season, refresh the dropdown and auto-select the new group
  // (which the effect above then hydrates into ticket groups).
  const onSeasonCreated = useCallback(
    (eventGroupId: string) => {
      setShowCreateSeasonModal(false);
      queryClient.invalidateQueries({
        queryKey: [GET_EVENT_GROUPS_QUERY_KEY],
      });
      setSelectedGroupId(eventGroupId);
    },
    [queryClient]
  );

  const onEventsSelect = useCallback<OnEventsSelectCallback>(
    (data) => {
      clearErrors('ticketGroups');

      const selectedViagogoVirtualIds = new Set<string>(
        ticketGroups
          .map((tg) => tg.event?.virtId)
          .filter((id): id is string => id != null)
      );

      const unselectedViagogoVirtualIds = new Set<string>();
      let newTicketGroups = [...(ticketGroups || [])];
      const startId = newBigIntId();

      data.forEach(({ event, isSelected, performer, venue }, i) => {
        if (isSelected) {
          // skip events that are already selected
          if (!selectedViagogoVirtualIds.has(event.virtId)) {
            if (ticketGroupFromPartialPurchase == null) {
              // Copy source(s) if this is a "Copy tickets" flow (one group for the
              // per-ticket-group copy, or all of an event's groups for the event
              // level copy); otherwise add a single blank group for the event.
              const sources = sourceTicketGroups?.length
                ? sourceTicketGroups
                : sourceTicketGroup
                  ? [sourceTicketGroup]
                  : null;
              if (sources) {
                sources.forEach((src, j) => {
                  newTicketGroups.push(
                    copyTicketGroupToEvent({
                      source: src,
                      event,
                      performer,
                      venue,
                      currencyCode,
                      // Distinct id per (event, source) — i*100 leaves room for
                      // multiple source groups per picked event.
                      ticketGroupId: startId - i * 100 - j,
                    })
                  );
                });
              } else {
                newTicketGroups.push(
                  buildTicketGroupInput({
                    event,
                    performer,
                    venue,
                    currencyCode,
                    ticketGroupId: startId - i,
                  })
                );
              }
            } else {
              newTicketGroups.push({
                ...ticketGroupFromPartialPurchase,
                ticketGroupId: startId - i,
                deliveryType: posChangedField(DeliveryType.InApp),
                currencyCode: posChangedField(currencyCode),
                viagogoVirtualId: event.virtId,
                eventMappingId: event.mpId,
                viagogoEventId: event.viagId,
                posEventId: event.posId,
                event: event,
                performer: performer,
                venue: venue,
                taxPaid: posChangedField(1), // This is to turn off taxExempt by default
              } as TicketGroupInput);
            }
          }
        } else {
          unselectedViagogoVirtualIds.add(event.virtId);
        }
      });

      if (unselectedViagogoVirtualIds.size > 0) {
        // If we unselect an event, that just mean we don't want to add this new event
        // this time, so only remove new ticket groups with this event (eventId is null)
        newTicketGroups = newTicketGroups?.filter(
          (tg) =>
            tg.numberOfTickets?.value ||
            (tg.event?.virtId &&
              !unselectedViagogoVirtualIds.has(tg.event.virtId))
        );
      }

      setValue('ticketGroups', newTicketGroups);
    },
    [
      clearErrors,
      currencyCode,
      setValue,
      ticketGroups,
      ticketGroupFromPartialPurchase,
      sourceTicketGroup,
      sourceTicketGroups,
    ]
  );

  const onDeleteNumEventsSelected = useCallback(() => {
    setValue('ticketGroups', []);
  }, [setValue]);

  return (
    <>
      <ModalBodyDataContainer>
        {formState.errors.ticketGroups && (
          <FieldError>{formState.errors.ticketGroups.message}</FieldError>
        )}
        {hasEventGroupFeature && (
          <div className={styles.seasonGroup}>
            <Checkbox
              checked={fromEventGroup}
              onChange={onToggleFromEventGroup}
              labelPosition="right"
              label={<Content id={ContentId.CreatePoFromSeasonGroup} />}
            />
          </div>
        )}
        {fromEventGroup ? (
          <Stack direction="column" gap="m">
            <Stack direction="row" gap="m" alignItems="center" width="full">
              <div style={{ flex: 1 }}>
                <PosSelect
                  showFullWidth
                  value={selectedGroupId ?? undefined}
                  valueOptionsContent={eventGroupOptions}
                  onChange={(value) => setSelectedGroupId(value || null)}
                  placeholderText={ContentId.SelectSeasonGroup}
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setShowCreateSeasonModal(true)}
              >
                <Content id={ContentId.CreateSeason} />
              </Button>
            </Stack>
            {isGroupLoading && <PosSpinner />}
            {!isGroupLoading && Boolean(groupTicketGroups.length) && (
              <Virtuoso
                style={{ width: '100%', height: window.innerHeight - 300 }}
                data={groupTicketGroups}
                overscan={window.innerHeight}
                itemContent={(_, tg) => {
                  if (!tg.event) {
                    return undefined;
                  }
                  return (
                    <EventSearchItem
                      key={tg.event?.virtId}
                      event={tg.event!}
                      venue={tg.venue}
                      performer={tg.performer}
                      onChecked={noop}
                      displayOnly
                    />
                  );
                }}
              />
            )}
          </Stack>
        ) : (
          <EventSearch
            onEventsSelect={onEventsSelect}
            selectedEvents={selectedEventIds}
            searchTextPrefill={searchTextPrefillForPartialPurchase ?? undefined}
            numEventsSelected={new Set(selectedEventIds).size}
            onDeleteNumEventsSelected={onDeleteNumEventsSelected}
          />
        )}
      </ModalBodyDataContainer>
      {showCreateSeasonModal && (
        <CreateSeasonModal
          onClose={() => setShowCreateSeasonModal(false)}
          onSuccess={onSeasonCreated}
        />
      )}
    </>
  );
}
